import React, { memo, useEffect, useMemo, useState } from "react"
import { makeStyles } from "@material-ui/styles"
import moment from "moment"
import sortArray from "sort-array"
import { Box, Button } from "@material-ui/core"
import InfiniteScroll from "react-infinite-scroll-component"
import { NoItemsMessage, LoadingSpinner } from ".."
import { Job } from "./Job"
import { useAuth } from "../../services"
import { toId } from "../../utils"
import { IntervalRenderer } from "../IntervalRenderer"
import { JOB_PINNING } from "../../data"

const useStyles = makeStyles((theme) => ({
  jobList: {
    marginBottom: theme.spacing(1),
  },
  loading: {
    display: "flex",
    justifyContent: "center",
  },
}))

const completeDelayMs = 3000

const defaultPageSize = 8

const jobSort = {
  common: {
    computed: {
      jobType: (job) => (job.process ? "process" : "task"),
      steps: (job) => job.status.steps.length,
      repeat: (job) => (job.process ? job.process.repeat.type : 0),
      completed: (job) => job.status.completed && moment().diff(job.status.completedAt) > completeDelayMs,
      overdue: (job) =>
        (!job.status.completed || moment().diff(job.status.completedAt) < completeDelayMs) &&
        job.dueAt !== null &&
        moment().isAfter(moment(job.dueAt))
          ? 0
          : 1,
    },
  },
  options: [
    {
      id: "jobStatus",
      name: "Job status",
      by: ["completed", "overdue", "availableFrom", "dueAt", "title"],
    },
    {
      id: "availableFromAsc",
      name: "Start time (Ascending)",
      by: ["availableFrom", "title", "jobType"],
      order: ["asc", "asc", "asc"],
    },
    {
      id: "availableFromDesc",
      name: "Start time (Descending)",
      by: ["availableFrom", "title", "jobType"],
      order: ["desc", "asc", "asc"],
    },
  ],
}

const JobListLoading = () => {
  const classes = useStyles()
  return (
    <div className={classes.loading}>
      <LoadingSpinner size={60} />
    </div>
  )
}

const listHasSomeById = (list1, list2) => list1.some((item1) => list2.find((item2) => toId(item2) === toId(item1)))

const isTimeBasedPinning = (job) =>
  [JOB_PINNING.MINUTES, JOB_PINNING.HOURS, JOB_PINNING.DAYS].includes(job.pinning.type)

const filterActiveTimeBasedPinning = (job, date) => {
  // not time based pinning?
  if (!isTimeBasedPinning(job)) {
    return true
  }

  // current date/time is before pinning until
  if (moment().isSameOrBefore(moment(job.pinning.until))) {
    return true
  }

  // pinning until is after request date
  if (moment(date).add(1, "day").startOf("day").isSameOrBefore(moment(job.pinning.until))) {
    return true
  }

  return false
}

const filterData = (
  data,
  location,
  date,
  filterKeywords,
  isMe,
  toUsers,
  toGroups,
  includeEveryoneJobs,
  byUsers,
  byGroups,
  hideTasks,
  hideProcesses,
  hideCompleted,
  hideFuture,
  hideHidden,
  isFuture
) =>
  data.jobs.items.filter(
    (job) =>
      ((job.task && !hideTasks) || (job.process && !hideProcesses)) &&
      (!job.status.completed || !hideCompleted) &&
      job.location.id === location.id &&
      (moment().isSameOrAfter(job.availableFrom) || !hideFuture || isFuture) &&
      (!hideHidden || filterActiveTimeBasedPinning(job, date)) &&
      (!filterKeywords.length || filterKeywords.every((keyword) => `${job.title}`.toLowerCase().includes(keyword))) &&
      ((isMe &&
        ((includeEveryoneJobs && !job.users.length && !job.groups.length) ||
          listHasSomeById(job.users, toUsers) ||
          listHasSomeById(job.groups, toGroups))) ||
        ((!toUsers ||
          !toUsers.length ||
          (includeEveryoneJobs && !job.users.length) ||
          (toUsers && toUsers.length && listHasSomeById(job.users, toUsers))) &&
          (!toGroups ||
            !toGroups.length ||
            (includeEveryoneJobs && !job.groups.length) ||
            (toGroups && toGroups.length && listHasSomeById(job.groups, toGroups))) &&
          (((!byUsers || !byUsers.length) && (!byGroups || !byGroups.length)) ||
            (byUsers && byUsers.length && byUsers.some((user) => toId(job.author) === toId(user))) ||
            (byGroups &&
              byGroups.length &&
              job.author.groups?.some((group) =>
                byGroups.find((assignedGroup) => toId(assignedGroup) === toId(group))
              )))))
  )

const JobList = memo(function JobList({
  loading,
  data,
  networkStatus,
  onEdit,
  filters,
  date,
  compact,
  empty,
  showEndMessage = true,
  showShowMore = false,
  pageSize = defaultPageSize,
}) {
  const classes = useStyles()
  const { location, principal } = useAuth()
  const [total, setTotal] = useState(0)
  const [limit, setLimit] = useState(pageSize)

  useEffect(() => {
    setLimit(pageSize)
  }, [filters, pageSize])

  const handleLoadMore = (isShow) => {
    if (!isShow && showShowMore) {
      return
    }
    setLimit(Math.min(limit + pageSize, data?.jobs.items.length || 0))
  }

  const items = useMemo(() => {
    if (!data) {
      return []
    }

    let result
    if (filters) {
      const {
        tab,
        filterText,
        toHasEveryone,
        toUsers,
        toGroups,
        byHasEveryone,
        byUsers,
        byGroups,
        hideTasks,
        hideProcesses,
        hideCompleted,
        hideFuture,
        hideHidden,
        isFuture,
        sort,
      } = filters

      const filterKeywords = filterText
        ? filterText
            .split(" ")
            .map((keyword) => keyword.toLowerCase().trim())
            .filter((k) => k)
        : []

      result = filterData(
        data,
        location,
        date,
        filterKeywords,
        tab === "me",
        tab === "me" ? [{ id: principal.userID }] : toHasEveryone ? [] : toUsers,
        tab === "me" ? [...principal.groups] : toHasEveryone ? [] : toGroups,
        tab === "me",
        tab === "me" || byHasEveryone ? [] : byUsers,
        tab === "me" || byHasEveryone ? [] : byGroups,
        hideTasks,
        hideProcesses,
        hideCompleted,
        hideFuture,
        hideHidden,
        isFuture
      )

      if (sort) {
        const sortOption = jobSort.options.find((option) => option.id === sort)
        sortArray(result, { ...jobSort.common, ...sortOption })
      }
    } else {
      result = data.jobs.items
    }

    if (result.length !== total) {
      setTotal(result.length)
    }

    return result.slice(0, limit)
  }, [data, date, filters, limit, location, principal.groups, principal.userID, total])

  if (!data?.jobs.items.length && loading && networkStatus !== 6) {
    return <JobListLoading />
  }
  if (!data || (data && (!data.jobs || !data.jobs.items.length))) {
    return <div className={classes.jobList}>{empty || <NoItemsMessage>No current jobs</NoItemsMessage>}</div>
  }

  const hasMore = total > limit

  return (
    <IntervalRenderer delay={items.length ? 1000 : 0}>
      {() => (
        <Box mb={5}>
          <InfiniteScroll
            dataLength={items.length}
            next={handleLoadMore}
            hasMore={hasMore}
            loader={!showShowMore && <JobListLoading />}
            endMessage={
              !loading &&
              showEndMessage && (
                <NoItemsMessage>
                  {items.length || "No"} matching job{items.length > 1 || !items.length ? "s" : ""}
                </NoItemsMessage>
              )
            }
            style={{ overflow: "visible" }}
          >
            <Box data-cy="Box-jobs">
              <JobItems items={items} date={date} compact={compact} onEdit={onEdit} />
            </Box>
          </InfiniteScroll>
          {showShowMore && hasMore && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Button variant="outlined" onClick={() => handleLoadMore(true)}>
                Show More
              </Button>
            </Box>
          )}
        </Box>
      )}
    </IntervalRenderer>
  )
})

const JobItems = memo(function JobItems({ items, date, compact, onEdit }) {
  return items.map((job, index) => (
    <Job key={job.id} job={job} onEdit={onEdit} date={date} compact={compact} cy={`Job-${index}`} />
  ))
})

export { JobList, jobSort }
